import { useEffect, useMemo, useState } from 'react';
import { SettingRow } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Switch, Textarea, Checkbox } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';

interface Form {
  enabled: boolean;
  block_scanners: boolean;
  block_crawlers: boolean;
  // Allow categories — kept un-blocked even when crawler blocking is on.
  allow_search_engines: boolean;
  allow_social: boolean;
  allow_monitors: boolean;
  verified_bypass_enabled: boolean;
  verified_categories: string[];
  custom_block_patterns: string[];
}
const DEFAULTS: Form = {
  enabled: false,
  block_scanners: false,
  block_crawlers: false,
  allow_search_engines: false,
  allow_social: false,
  allow_monitors: false,
  verified_bypass_enabled: false,
  verified_categories: [],
  custom_block_patterns: [],
};
const CATEGORIES = [
  { value: 'search_engine', label: 'Search engines' },
  { value: 'social_media', label: 'Social media' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'ai_crawler', label: 'AI crawlers' },
  { value: 'other', label: 'Other' },
];

function hydrate(c: Record<string, unknown>): Form {
  return {
    enabled: Boolean(c.enabled),
    block_scanners: Boolean(c.block_scanners),
    block_crawlers: Boolean(c.block_crawlers),
    // Allow categories default to true when the field is absent (legacy parity).
    allow_search_engines: c.allow_search_engines == null ? true : Boolean(c.allow_search_engines),
    allow_social: c.allow_social == null ? true : Boolean(c.allow_social),
    allow_monitors: c.allow_monitors == null ? true : Boolean(c.allow_monitors),
    verified_bypass_enabled: Boolean(c.verified_bypass_enabled),
    verified_categories: Array.isArray(c.verified_categories) ? (c.verified_categories as string[]) : [],
    custom_block_patterns: Array.isArray(c.custom_block_patterns) ? (c.custom_block_patterns as string[]) : [],
  };
}

export function VerifiedBotsTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'bot', { vendorMarkers: ['enabled', 'block_scanners'] });
  const isConfigured = !!cfg;
  const [form, setForm] = useState<Form>(DEFAULTS);
  const [snap, setSnap] = useState<Form>(DEFAULTS);
  useEffect(() => {
    const next = cfg ? hydrate(cfg) : DEFAULTS;
    setForm(next);
    setSnap(next);
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, snap), [form, snap]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCat = (cat: string) =>
    set('verified_categories', form.verified_categories.includes(cat) ? form.verified_categories.filter((c) => c !== cat) : [...form.verified_categories, cat]);

  const update = useUpdateCdnConfig(domainId, 'bot', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'verified_bots', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Bot settings saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save', description: errorMessage(e, 'Could not save') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'bot', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'verified_bots', 'delete');
      show({ tone: 'success', title: 'Bot settings removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove', description: errorMessage(e, 'Could not remove') }),
  });
  const busy = update.isPending || remove.isPending;

  return (
    <ConfigFormShell
      title="Verified Bots"
      description={`Allow verified bots and block scanners/crawlers for ${domain}.`}
      isLoading={isLoading}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      statusLabel={form.enabled ? 'Enabled' : undefined}
      onSave={() => update.mutate({ ...form, custom_block_patterns: form.custom_block_patterns.map((s) => s.trim()).filter(Boolean) } as unknown as Record<string, unknown>)}
      onRemove={() => remove.mutate()}
      onCancel={() => setForm(snap)}
      drift={<CdnAspectDrift domainId={domainId} path="bot" />}
    >
      <Card>
        <CardHeader>
          <CardTitle>Bot management</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <SettingRow title="Enabled" description="Master switch for bot handling." control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
          <SettingRow title="Block scanners" description="Block security scanners (sqlmap, nikto, nmap, etc.)." control={<Switch checked={form.block_scanners} onCheckedChange={(v) => set('block_scanners', v)} />} />
          <SettingRow title="Block crawlers" description="Block generic, non-search-engine crawlers." control={<Switch checked={form.block_crawlers} onCheckedChange={(v) => set('block_crawlers', v)} />} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Allow categories</CardTitle>
          <CardDescription>These categories are not blocked even when crawler blocking is on.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <SettingRow title="Search engines" control={<Switch checked={form.allow_search_engines} onCheckedChange={(v) => set('allow_search_engines', v)} />} />
          <SettingRow title="Social media" control={<Switch checked={form.allow_social} onCheckedChange={(v) => set('allow_social', v)} />} />
          <SettingRow title="Monitors" control={<Switch checked={form.allow_monitors} onCheckedChange={(v) => set('allow_monitors', v)} />} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verified bot bypass</CardTitle>
          <CardDescription>Bypasses WAF, rate limiting, and bot blocking for verified bots in the selected categories.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <SettingRow title="Enable bypass" control={<Switch checked={form.verified_bypass_enabled} onCheckedChange={(v) => set('verified_bypass_enabled', v)} />} />
          {form.verified_bypass_enabled && (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {CATEGORIES.map((c) => (
                <label key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-lg)', cursor: 'pointer' }}>
                  <Checkbox checked={form.verified_categories.includes(c.value)} onCheckedChange={() => toggleCat(c.value)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Custom block patterns</CardTitle>
          <CardDescription>One regex per line. Matching User-Agents are blocked.</CardDescription>
        </CardHeader>
        <CardBody>
          <Field label="Patterns">
            {(p) => (
              <Textarea
                {...p}
                rows={4}
                value={form.custom_block_patterns.join('\n')}
                placeholder="(?i)evil-bot"
                onChange={(e) => set('custom_block_patterns', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }}
              />
            )}
          </Field>
        </CardBody>
      </Card>
    </ConfigFormShell>
  );
}
